import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ProductCard from '../components/ui/ProductCard';
import CategoryCard from '../components/ui/CategoryCard';
import { useQuery } from 'react-query';
import { categoriesAPI, newsletterAPI } from '../services/api';
import InlineEditor from '../components/ui/InlineEditor';
import InlineTextArea from '../components/ui/InlineTextArea';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../services/settingsAPI';


const HomePage = () => {
  const { t, language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [siteSettings, setSiteSettings] = useState(null);
  const { user } = useAuth();
  const { getSetting: getGlobalSetting, getLocalizedSetting: getGlobalLocalizedSetting, setSiteSettings: setGlobalSiteSettings } = useSiteSettings();

  // Our Story image (same as About page)
  const defaultStory = 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&h=600&fit=crop&crop=center';
  const storyImage = getGlobalSetting('about_story_image_url', defaultStory);
  const storyFileInputRef = useRef(null);
  const [storyUploading, setStoryUploading] = useState(false);
  const triggerStoryPick = () => {
    if (user?.role !== 'admin') return;
    if (storyFileInputRef.current) storyFileInputRef.current.click();
  };
  const handleStoryFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setStoryUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('photo', file);
      const res = await fetch('/api/demo-photos/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload échoué');
      }
      const data = await res.json();
      const url = data?.file?.url;
      if (!url) throw new Error('URL du fichier non reçue');

      await settingsAPI.update('about_story_image_url', url, token);
      setGlobalSiteSettings(prev => ({ ...prev, about_story_image_url: url }));
      window.dispatchEvent(new CustomEvent('settingUpdated', { detail: { key: 'about_story_image_url', value: url } }));
    } catch (err) {
      console.error(err);
    } finally {
      setStoryUploading(false);
      if (storyFileInputRef.current) storyFileInputRef.current.value = '';
    }
  };

  // Fetch site settings for homepage texts
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings/category/homepage');
        const data = await response.json();
        if (data?.success) {
          const map = {};
          for (const s of data.settings) {
            map[s.setting_key] = s.setting_value;
          }
          setSiteSettings(map);
        }
      } catch (e) {
        console.error('Error fetching site settings:', e);
      }
    };
    fetchSettings();

    // Listen for setting updates from inline editors
    const handleSettingUpdate = (event) => {
      const { key, value } = event.detail;
      setSiteSettings(prev => ({
        ...prev,
        [key]: value
      }));
    };

    window.addEventListener('settingUpdated', handleSettingUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('settingUpdated', handleSettingUpdate);
    };
  }, []);

  // Helper to read localized/non-localized settings with safe fallbacks
  const S = (baseKey, options = { localized: true }) => {
    if (!siteSettings) return null;
    if (options.localized === false) {
      return siteSettings[baseKey] ?? null;
    }
    const suffix = language === 'fr' ? '_fr' : '_en';
    return siteSettings[`${baseKey}_${language}`] // exact match like 'something_fr' already
      ?? siteSettings[`${baseKey}${suffix}`]
      ?? null;
  };

  // Helper to get localized or non-localized setting
  const getLocalizedSetting = (baseKey, fallback = null) => {
    if (!siteSettings) return fallback;
    const localizedKey = `${baseKey}_${language}`;
    return siteSettings[localizedKey] ?? siteSettings[baseKey] ?? fallback;
  };

  // Helper to get a setting, potentially localized
  const getSetting = (baseKey, fallback = null) => {
    if (!siteSettings) return fallback;
    const localizedKey = `${baseKey}_${language}`;
    return siteSettings[localizedKey] ?? siteSettings[baseKey] ?? fallback;
  };

  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useQuery(
    ['categories'],
    async () => {
      const response = await categoriesAPI.getAll();
      return response.data.categories || [];
    },
    { retry: (count, err) => (err?.status === 429 ? count < 0 : count < 2), refetchOnWindowFocus: false }
  );

  const categories = categoriesData || [];

  // Fetch demo photos for hero section
  const { data: demoPhotosData, isLoading: demoPhotosLoading } = useQuery(
    ['demoPhotos'],
    async () => {
      try {
        // Fetch all demo photos, not just hero_slide
        const response = await fetch('/api/demo-photos');
        const ct = response.headers.get('content-type') || '';
        const data = ct.includes('application/json') ? await response.json() : await response.text().then(t => { try { return JSON.parse(t); } catch { return { success: false, message: t }; } });
        console.log('Demo photos API response:', data);
        return data.success ? data.photos : [];
      } catch (error) {
        console.error('Error fetching demo photos:', error);
        return [];
      }
    },
    { retry: (count, err) => (err?.status === 429 ? count < 0 : count < 1), refetchOnWindowFocus: false }
  );

  // Use demo photos from API or fallback to hardcoded ones
  const heroSlides = useMemo(() => {
    if (demoPhotosData && demoPhotosData.length > 0) {
      console.log('Raw demo photos data:', demoPhotosData);
      
      const slides = demoPhotosData.map(photo => {
        const slide = {
          title: photo.title_fr || photo.title_en || 'Demo Title',
          subtitle: photo.subtitle_fr || photo.subtitle_en || 'Demo Subtitle',
          cta: photo.button_text_fr || photo.button_text_en || '',
          image: photo.image_url || 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=1200&h=600&fit=crop&crop=center',
          bgGradient: "from-iranian-900 via-gray-800 to-gray-900",
          buttonLink: photo.button_link || '/products'
        };
        
        console.log('Created slide:', {
          photoId: photo.id,
          name: photo.name,
          type: photo.type,
          button_link: photo.button_link,
          finalButtonLink: slide.buttonLink,
          title: slide.title
        });
        
        return slide;
      });
      
      console.log('HeroSlides created:', slides.length, 'slides');
      console.log('Final slides array:', slides.map(slide => ({
        title: slide.title,
        buttonLink: slide.buttonLink
      })));
      return slides;
    }
    console.log('No demo photos data');
    return [];
  }, [demoPhotosData]);

  // Ensure currentSlide is always 0 when there's only 1 photo
  useEffect(() => {
    if (heroSlides.length === 1) {
      setCurrentSlide(0);
      console.log('Single photo detected, setting currentSlide to 0');
    }
  }, [heroSlides.length]);

  // Log current slide info
  useEffect(() => {
    if (heroSlides.length > 0) {
      const currentSlideData = heroSlides[currentSlide];
      console.log('Current slide:', {
        index: currentSlide,
        name: currentSlideData?.title,
        buttonLink: currentSlideData?.buttonLink,
        type: currentSlideData?.type
      });
    }
  }, [currentSlide, heroSlides]);

  useEffect(() => {
    // Only run carousel timer if there are multiple slides
    if (heroSlides.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [heroSlides.length]);

  const nextSlide = () => {
    if (heroSlides.length > 1) {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      console.log('Next slide clicked, new slide:', (currentSlide + 1) % heroSlides.length);
    }
  };

  const prevSlide = () => {
    if (heroSlides.length > 1) {
      setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
      console.log('Previous slide clicked, new slide:', (currentSlide - 1 + heroSlides.length) % heroSlides.length);
    }
  };

  const goToSlide = (index) => {
    if (heroSlides.length > 1) {
      setCurrentSlide(index);
      console.log('Dot clicked, going to slide:', index);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      
      {/* Hero Section - Only show when there are demo photos */}
      {heroSlides.length > 0 ? (
        <section className="relative h-screen overflow-hidden">
          {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            <div className={`absolute inset-0 bg-gradient-to-r ${slide.bgGradient}`} />
            <img
              src={slide.image}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
            {/* Mobile magnifier anchored to the right button area (not overlaying main subject) */}
            <div className="lg:hidden absolute right-4 bottom-24 w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-2 border-yellow-500/40 shadow-2xl">
              <div
                className="w-full h-full"
                style={{
                  backgroundImage: `url(${slide.image})`,
                  backgroundSize: '200% 200%',
                  backgroundPosition: '70% 60%'
                }}
              />
              <div className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-yellow-300/40 shadow-inner" />
            </div>
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-white"
                  >
                    <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                      {slide.title.split(' ').map((word, i) => (
                        <span key={i} className={i === 1 ? 'text-yellow-500' : ''}>
                          {word}{' '}
                        </span>
                      ))}
                    </h1>
                    <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                      {slide.subtitle}
                    </p>
                    {slide.cta ? (
                      <Link
                        to={slide.buttonLink || "/products"}
                        className="inline-flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-8 py-4 rounded-lg transition-colors duration-300 group"
                        onClick={() => console.log('Button clicked:', { 
                          slideIndex: index,
                          slideName: slide.title, 
                          buttonLink: slide.buttonLink, 
                          fallback: slide.buttonLink || "/products",
                          currentSlide: currentSlide,
                          totalSlides: heroSlides.length
                        })}
                      >
                        <span>{slide.cta}</span>
                        <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ) : null}
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="hidden lg:block"
                  >
                    <div className="relative min-h-[28rem]">
                      {/* Desktop magnifier preview anchored bottom-right */}
                      <div className="absolute bottom-4 right-0 w-80 h-80 xl:w-96 xl:h-96 rounded-full overflow-hidden border-4 border-yellow-500/40 shadow-2xl">
                        <div
                          className="w-full h-full"
                          style={{
                            backgroundImage: `url(${slide.image})`,
                            backgroundSize: '200% 200%',
                            backgroundPosition: '55% 45%'
                          }}
                        />
                        <div className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-yellow-300/40 shadow-inner" />
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows - Only show when there are multiple slides */}
        {console.log('Rendering navigation - heroSlides.length:', heroSlides.length)}
        {heroSlides.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl z-20"
            >
              <ChevronLeft size={28} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl z-20"
            >
              <ChevronRight size={28} />
            </button>
          </>
        )}

        {/* Dots Indicator - Only show when there are multiple slides */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-4 h-4 rounded-full transition-all duration-300 shadow-lg hover:scale-110 ${
                  index === currentSlide 
                    ? 'bg-yellow-500 shadow-yellow-500/50' 
                    : 'bg-gray-400/70 hover:bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}
      </section>
      ) : null}

      {/* Categories & Featured Products Combined Section */}
      <section className="py-16 bg-gradient-to-b from-gray-800 to-gray-900">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          {/* Categories */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              <InlineEditor settingKey="categories_title_fr" defaultValue="Nos Catégories">
                {S('categories_title') || t('home.categories.title')}
              </InlineEditor>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              <InlineTextArea settingKey="categories_subtitle_fr" defaultValue="Découvrez notre gamme complète de bijoux élégants">
                {S('categories_subtitle') || t('home.categories.subtitle')}
              </InlineTextArea>
            </p>
          </motion.div>

          {categoriesLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
            </div>
          ) : categoriesError ? (
            <div className="text-center py-8">
              <p className="text-red-400">Erreur lors du chargement des catégories</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400">Aucune catégorie disponible</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-16">
              {categories.slice(0, 6).map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`
                    ${index % 3 !== 2 ? 'border-r border-[rgb(128,128,128)]' : ''} 
                    ${index < 3 ? 'border-b border-[rgb(128,128,128)]' : ''}
                  `}
                >
                  <CategoryCard category={category} />
                </motion.div>
              ))}
            </div>
          )}

          {/* Featured Products section removed */}
        </div>
      </section>

      {/* About & Stats Combined Section */}
      <section className="py-16 bg-gray-800">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          {/* About Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-8">
                <InlineEditor settingKey="homepage_our_story_title_fr" defaultValue="Notre Histoire">
                  {getLocalizedSetting('homepage_our_story_title', 'Notre Histoire')}
                </InlineEditor>
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                <InlineTextArea settingKey="homepage_our_story_description_fr" defaultValue="Mahja Bijoux combine l'élégance française avec la richesse culturelle iranienne pour créer des pièces uniques qui racontent une histoire.">
                  {getLocalizedSetting('homepage_our_story_description', 'Mahja Bijoux combine l\'élégance française avec la richesse culturelle iranienne pour créer des pièces uniques qui racontent une histoire.')}
                </InlineTextArea>
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-300 text-lg">
                    <InlineEditor settingKey="homepage_our_story_feature1_fr" defaultValue="Matériaux de qualité exceptionnelle">
                      {getLocalizedSetting('homepage_our_story_feature1', 'Matériaux de qualité exceptionnelle')}
                    </InlineEditor>
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-300 text-lg">
                    <InlineEditor settingKey="homepage_our_story_feature2_fr" defaultValue="Artisanat traditionnel iranien">
                      {getLocalizedSetting('homepage_our_story_feature2', 'Artisanat traditionnel iranien')}
                    </InlineEditor>
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-300 text-lg">
                    <InlineEditor settingKey="homepage_our_story_feature3_fr" defaultValue="Design moderne et élégant">
                      {getLocalizedSetting('homepage_our_story_feature3', 'Design moderne et élégant')}
                    </InlineEditor>
                  </span>
                </div>
              </div>

              <Link
                to="/products"
                className="inline-flex items-center space-x-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-8 py-4 rounded-lg transition-colors duration-300 text-lg"
              >
                <span>
                  <InlineEditor settingKey="about_discover_button_fr" defaultValue="Découvrir nos collections">
                    {getGlobalLocalizedSetting('about_discover_button', 'Découvrir nos collections')}
                  </InlineEditor>
                </span>
                <ArrowRight size={20} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                <img
                  src={storyImage}
                  alt="Persian Jewelry"
                  className="w-full h-96 object-cover rounded-2xl shadow-2xl"
                />
                {user?.role === 'admin' && (
                  <div className="absolute -bottom-6 -right-6 bg-yellow-500 text-gray-900 px-6 py-3 rounded-xl font-bold text-lg cursor-pointer" onClick={triggerStoryPick}>
                    {storyUploading ? 'Téléversement…' : 'Changer la photo'}
                  </div>
                )}
                <input ref={storyFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleStoryFileChange} />
              </div>
              {null}
            </motion.div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center mt-10 md:mt-12 lg:mt-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-900 rounded-xl p-6 hover:bg-gray-800 transition-colors duration-300"
            >
              <div className="text-4xl font-bold text-yellow-500 mb-2">
                <InlineEditor settingKey="stats_bijoux_count" defaultValue="500+">
                  {S('stats_bijoux_count', { localized: false }) || '500+'}
                </InlineEditor>
              </div>
              <div className="text-gray-300">
                <InlineEditor settingKey="stats_bijoux_label_fr" defaultValue="Bijoux Uniques">
                  {S('stats_bijoux_label') || 'Bijoux Uniques'}
                </InlineEditor>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-900 rounded-xl p-6 hover:bg-gray-800 transition-colors duration-300"
            >
              <div className="text-4xl font-bold text-yellow-500 mb-2">
                <InlineEditor settingKey="stats_years_count" defaultValue="15">
                  {S('stats_years_count', { localized: false }) || '15'}
                </InlineEditor>
              </div>
              <div className="text-gray-300">
                <InlineEditor settingKey="stats_years_label_fr" defaultValue="Années d'Excellence">
                  {S('stats_years_label') || "Années d'Excellence"}
                </InlineEditor>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gray-900 rounded-xl p-6 hover:bg-gray-800 transition-colors duration-300"
            >
              <div className="text-4xl font-bold text-yellow-500 mb-2">
                <InlineEditor settingKey="stats_clients_count" defaultValue="2000+">
                  {S('stats_clients_count', { localized: false }) || '2000+'}
                </InlineEditor>
              </div>
              <div className="text-gray-300">
                <InlineEditor settingKey="stats_clients_label_fr" defaultValue="Clients Satisfaits">
                  {S('stats_clients_label') || 'Clients Satisfaits'}
                </InlineEditor>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose TOLOU Section */}
      <section className="py-16 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              <InlineEditor settingKey="homepage_why_choose_title_fr" defaultValue="Pourquoi choisir TOLOU ?">
                {getLocalizedSetting('homepage_why_choose_title', 'Pourquoi choisir TOLOU ?')}
              </InlineEditor>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              <InlineTextArea settingKey="homepage_why_choose_subtitle_fr" defaultValue="Découvrez ce qui rend nos bijoux exceptionnels et uniques">
                {getLocalizedSetting('homepage_why_choose_subtitle', 'Découvrez ce qui rend nos bijoux exceptionnels et uniques')}
              </InlineTextArea>
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 hover:from-yellow-500/10 hover:to-yellow-600/10 transition-all duration-300 border border-gray-700 hover:border-yellow-500/50"
            >
              <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-yellow-500/30 transition-colors duration-300">
                <div className="text-3xl">💎</div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                <InlineEditor settingKey="homepage_why_choose_card1_title_fr" defaultValue="Qualité Premium">
                  {getLocalizedSetting('homepage_why_choose_card1_title', 'Qualité Premium')}
                </InlineEditor>
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                <InlineTextArea settingKey="homepage_why_choose_card1_description_fr" defaultValue="Matériaux nobles et pierres précieuses sélectionnés avec soin pour garantir excellence et durabilité." rows={3}>
                  {getLocalizedSetting('homepage_why_choose_card1_description', 'Matériaux nobles et pierres précieuses sélectionnés avec soin pour garantir excellence et durabilité.')}
                </InlineTextArea>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 hover:from-yellow-500/10 hover:to-yellow-600/10 transition-all duration-300 border border-gray-700 hover:border-yellow-500/50"
            >
              <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-yellow-500/30 transition-colors duration-300">
                <div className="text-3xl">🎨</div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                <InlineEditor settingKey="homepage_why_choose_card2_title_fr" defaultValue="Design Unique">
                  {getLocalizedSetting('homepage_why_choose_card2_title', 'Design Unique')}
                </InlineEditor>
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                <InlineTextArea settingKey="homepage_why_choose_card2_description_fr" defaultValue="Créations originales mêlant élégance française et richesse culturelle iranienne pour des pièces uniques." rows={3}>
                  {getLocalizedSetting('homepage_why_choose_card2_description', 'Créations originales mêlant élégance française et richesse culturelle iranienne pour des pièces uniques.')}
                </InlineTextArea>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 hover:from-yellow-500/10 hover:to-yellow-600/10 transition-all duration-300 border border-gray-700 hover:border-yellow-500/50"
            >
              <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-yellow-500/30 transition-colors duration-300">
                <div className="text-3xl">✨</div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                <InlineEditor settingKey="homepage_why_choose_card3_title_fr" defaultValue="Artisanat Expert">
                  {getLocalizedSetting('homepage_why_choose_card3_title', 'Artisanat Expert')}
                </InlineEditor>
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                <InlineTextArea settingKey="homepage_why_choose_card3_description_fr" defaultValue="Savoir-faire traditionnel et techniques modernes pour créer des bijoux d'exception." rows={3}>
                  {getLocalizedSetting('homepage_why_choose_card3_description', "Savoir-faire traditionnel et techniques modernes pour créer des bijoux d'exception.")}
                </InlineTextArea>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 hover:from-yellow-500/10 hover:to-yellow-600/10 transition-all duration-300 border border-gray-700 hover:border-yellow-500/50"
            >
              <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-yellow-500/30 transition-colors duration-300">
                <div className="text-3xl">🚚</div>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                <InlineEditor settingKey="homepage_why_choose_card4_title_fr" defaultValue="Livraison Rapide">
                  {getLocalizedSetting('homepage_why_choose_card4_title', 'Livraison Rapide')}
                </InlineEditor>
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                <InlineTextArea settingKey="homepage_why_choose_card4_description_fr" defaultValue="Service client exceptionnel et livraison sécurisée pour une expérience d'achat parfaite." rows={3}>
                  {getLocalizedSetting('homepage_why_choose_card4_description', "Service client exceptionnel et livraison sécurisée pour une expérience d'achat parfaite.")}
                </InlineTextArea>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 to-yellow-600">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 xl:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              <InlineEditor settingKey="newsletter_title_fr" defaultValue="Restez informé de nos nouveautés">
                {S('newsletter_title') || 'Restez informé de nos nouveautés'}
              </InlineEditor>
            </h2>
            <p className="text-lg text-gray-800 mb-8">
              <InlineTextArea settingKey="newsletter_subtitle_fr" defaultValue="Recevez en avant-première nos nouvelles collections et offres exclusives">
                {S('newsletter_subtitle') || 'Recevez en avant-première nos nouvelles collections et offres exclusives'}
              </InlineTextArea>
            </p>
            
            <NewsletterForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 

// Inline component: Newsletter subscription form
function NewsletterForm() {
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState("Merci ! Vous êtes abonné à la newsletter.");

  const onSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (!isValid) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await newsletterAPI.subscribe(value);
      setMessage(res.data?.message || "Merci ! Vous êtes abonné à la newsletter.");
      setOpen(true);
      setEmail('');
    } catch (err) {
      setError(err?.payload?.message || err?.message || "Échec de l'abonnement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
        <div className="flex-1">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre adresse email"
            className="w-full px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-gray-900 focus:outline-none text-gray-900"
          />
          {error ? <p className="mt-2 text-sm text-gray-900 bg-red-100/70 rounded px-3 py-2">{error}</p> : null}
        </div>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? 'Envoi…' : "S'abonner"}
        </button>
      </form>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative mx-4 w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-900">Abonnement confirmé</h3>
            </div>
            <div className="bg-gray-900 px-6 py-6 border-t border-gray-800">
              <p className="text-gray-200 text-base">{message}</p>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setOpen(false)}
                  className="px-5 py-2 bg-yellow-500 text-gray-900 font-semibold rounded-lg hover:bg-yellow-400"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}